import { IBasePickerStyles, ITag, ITagPickerProps, TagPicker } from "@fluentui/react/lib/Pickers";
import { ISearchParameters } from "../../models";
import { ApiService } from "../../services";
import { isEmpty } from "../../tools";
import { useId } from "@fluentui/react-hooks";
import { ILabelProps, Label } from "@fluentui/react/lib/Label";
import { mergeStyles } from "@fluentui/react/lib/Styling";
import { useContext } from "react";

export interface IModel {
    key: string;
    name?: string | null;
}

export interface IPickerItem<T> extends ITag {
    data?: T;
}

export interface PickerProps<T> {
    service?: ApiService<T>;
    tagPickerProps?: ITagPickerProps;
    pickerStyles?: Partial<IBasePickerStyles>;
    pickOnlyOne?: boolean;
    inputTextColor?: string;
    placeholder?: string;
    disabled?: boolean;
    removeDuplicateKey?: boolean;
    selectedItems?: IPickerItem<T>[];
    maximumResults?: number;
    label?: string;
    labelProps?: ILabelProps;
    required?: boolean;
    defaultSelectedItems?: IPickerItem<T>[];
    displayOnFocus?: boolean;
    minimumTextLength?: number;
    onResolveSuggestions?: (filter: string, selectedItems?: IPickerItem<T>[] | undefined, service?: ApiService<T> | undefined) => IPickerItem<T>[] | PromiseLike<IPickerItem<T>[]>;
    onMapResults?: (data: T[]) => IPickerItem<T>[];
    onMapFilter?: (filter: string, defaultFilter: ISearchParameters) => ISearchParameters;
    onSelect?: (item: IPickerItem<T> | undefined) => IPickerItem<T> | Promise<IPickerItem<T>> | null;
    onChange?: (item: IPickerItem<T>[] | undefined) => void | Promise<void> | undefined;
}

const listContainsTagList = <T extends IModel>(item: IPickerItem<T>, selectedItems?: IPickerItem<T>[]) => {
    if (!selectedItems || !selectedItems.length || selectedItems.length === 0) {
        return false;
    }

    return selectedItems.some(compareTag => compareTag.key === item.key);
};

export const Picker = <T extends IModel>(props: PickerProps<T>) => {
    const pickerId = useId('inline-picker');

    const {
        service,
        pickOnlyOne = false,
        inputTextColor = "",
        disabled = false,
        removeDuplicateKey = true,
        selectedItems,
        placeholder,
        pickerStyles,
        maximumResults = 100,
        label,
        labelProps,
        required,
        defaultSelectedItems,
        displayOnFocus = true,
        minimumTextLength,
        onSelect,
        onChange,
        onResolveSuggestions,
        onMapFilter,
        onMapResults,
    } = props;

    const filterDuplicate = (items: IPickerItem<T>[], selectedItems?: IPickerItem<T>[]) => {
        const filterItems = [...items];
        return filterItems.filter(i => !listContainsTagList(i, selectedItems));
    };

    const onDefaultMapResults = (data: T[]): IPickerItem<T>[] => {
        if (!data || isEmpty(data)) {
            return [];
        }

        return data.map(d => ({ key: d.key, name: (d.name ? d.name : d.key), data: d }));
    }

    const _onResolveSuggestions = (filter: string, selectedItems?: IPickerItem<T>[] | undefined) => {
        if (minimumTextLength && (!filter || filter.length < minimumTextLength)) {
            return [];
        }

        if (onResolveSuggestions) {
            return onResolveSuggestions(filter, selectedItems, service);
        }

        if (service) {
            const promise = service(filter)
            return promise.then(results => {
              //  console.log(results)
                let result_search = [];
                results.forEach((p) => {
                    result_search.push({
                        name: p.givenName + ' ' + p.surname,
                        key: p.userPrincipalName,
                    });
                })
                return result_search;
                /*
                const items = onMapResults ? onMapResults(results.items) : onDefaultMapResults(results.items);
                if (items && removeDuplicateKey) {
                    return filterDuplicate(items, selectedItems)
                }

                return items;*/
            });
            /*
            const defaultFilter = {
                keyword: filter,
                pageNumber: 1,
                numberPerPage: maximumResults,
                status: 'active',
            };

            const mappedFilter = onMapFilter
                ? onMapFilter(filter, { ...defaultFilter })
                : { ...defaultFilter };

            const promise = service.search(mappedFilter);
            return promise.then(results => {
                const items = onMapResults ? onMapResults(results.items) : onDefaultMapResults(results.items);
                if (items && removeDuplicateKey) {
                    return filterDuplicate(items, selectedItems)
                }

                return items;
            });*/
        } else {
            return [
                {
                    name: "AAAAA",
                    key: "AAAAA",
                },
                {
                    name: "BBBBB",
                    key: "BBBBB",
                }
            ];
        }
    }

    const tagPickerProps: ITagPickerProps = {
        onResolveSuggestions: _onResolveSuggestions,
        inputProps: {
            placeholder: placeholder || 'Select',
            id: pickerId,
        },
        styles: pickerStyles,
    }

    if (pickOnlyOne) {
        tagPickerProps.itemLimit = 1;

        let pickerOnlyOneClassName = mergeStyles({
            '& span.ms-BasePicker-itemsWrapper': {
                width: '100%',
            }, '& .ms-BasePicker-text': {
                border: "1px solid #ced4da", minHeight: "40px", marginBottom: "10px", backgroundColor: inputTextColor, fontFamily: "tahoma"
                ,
                '& .ms-BasePicker-input': {
                    marginBottom: "3px",
                    fontFamily: "tahoma"
                }
            },
            '& .ms-TagItem': {
                width: '100%',
                maxWidth: '100%',
                '& .ms-TagItem-text': {
                    flexGrow: 1,
                    fontFamily: "tahoma"
                }
            }
        });

        tagPickerProps.className = pickerOnlyOneClassName;
    } else {
        let pickerMultiClassName = mergeStyles({
            '& .ms-BasePicker-text': {
                border: "1px solid #ced4da", minHeight: "40px", marginBottom: "10px", backgroundColor: inputTextColor, fontFamily: "tahoma"
                ,
                '& .ms-BasePicker-input': {
                    marginBottom: "3px",
                    fontFamily: "tahoma"
                }
            },
            '& .ms-TagItem': {
                '& .ms-TagItem-text': {
                    fontFamily: "tahoma"
                }
            }
        });

        tagPickerProps.className = pickerMultiClassName;
    }

    if (pickOnlyOne) {
    }

    if (displayOnFocus) {
        tagPickerProps.onEmptyResolveSuggestions = (selecteItems) => _onResolveSuggestions('', selecteItems);
    }

    const renderPicker = () => {
        return (
            <TagPicker
                {...tagPickerProps}
                onItemSelected={onSelect}
                defaultSelectedItems={defaultSelectedItems}
                onChange={onChange}
                disabled={disabled}
                selectedItems={selectedItems}
                resolveDelay={300}
            />
        );
    }

    return (
        <>
            {label ? (
                <div className='ms-TextField-wrapper'>
                    <Label {...labelProps} required={required} htmlFor={pickerId} disabled={disabled}>{label}</Label>
                    {renderPicker()}
                </div>
            ) : renderPicker()}

        </>
    );
};
